import { ScrollText } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { TableSkeleton } from '@/components/shared/loading';
import { Pagination, useCursorPages } from '@/components/shared/pagination';
import { SectionCard } from '@/components/shared/section-card';
import { ToneBadge } from '@/components/shared/tone-badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import type { Page } from '@/lib/types';
import { useAppCtx } from './app-layout';

type Entry = { id: string; action: string; actor_user_id: string | null; actor_ip: string | null; target_type: string | null; target_id: string | null; metadata: Record<string, unknown>; created_at: string };

export function AuditLog() {
  const { app } = useAppCtx();
  const pages = useCursorPages({
    queryKey: ['audit', app.id],
    fetchPage: (before, limit) => api<Page<Entry>>(`/admin/v1/apps/${app.id}/audit-log`, { query: { before, limit } }),
  });
  const q = pages.query;
  const rows = pages.rows;
  return (
    <div className="space-y-4">
      <SectionCard title="Audit log" description="Every change made to this application, newest first." flush className={q.isPlaceholderData ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        {q.isLoading ? <TableSkeleton /> : rows.length === 0 ? <EmptyState icon={ScrollText} title="No audit entries" /> : (
          <Table>
            <TableHeader>
              <TableRow><TableHead className="pl-5">When</TableHead><TableHead>Action</TableHead><TableHead>Target</TableHead><TableHead>Details</TableHead><TableHead className="pr-5">Actor IP</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="pl-5 text-muted-foreground">{fmtDate(e.created_at)}</TableCell>
                  <TableCell><ToneBadge tone="blue" className="font-mono">{e.action}</ToneBadge></TableCell>
                  <TableCell className="font-mono text-xs">{e.target_type ? `${e.target_type}:${e.target_id?.slice(0, 10)}` : '—'}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {/* max-width on a table cell is ignored, so long JSON would widen the whole page. */}
                    <div className="max-w-md truncate">{Object.keys(e.metadata).length ? JSON.stringify(e.metadata) : ''}</div>
                  </TableCell>
                  <TableCell className="pr-5 font-mono text-xs">{e.actor_ip ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pagination p={pages} noun="entries" />
      </SectionCard>
    </div>
  );
}
